import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "flowbite-react";
import { format } from "date-fns";
import CustomerInvoiceApi from "../../Api/CustomerInvoiceApi";
import { CustomerInvoiceDetail as InvoiceDetail } from "../../Models/CustomerInvoice";
import Loader from "../Common/Loader";
import PBLogo from "../../Assets/PriceBros-Logo.png";

const money = (n: number | null | undefined) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(n);

const fmtDate = (d: string | null | undefined) =>
  d ? format(new Date(d), "MMM d, yyyy") : "—";

export default function CustomerInvoiceDetail() {
  const { invoiceNumber } = useParams();
  const [data, setData] = useState<InvoiceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const id = "invoice-print-style";
    const style = document.createElement("style");
    style.id = id;
    style.textContent = `@media print {
      body * { visibility: hidden !important; }
      #invoice-print-area, #invoice-print-area * { visibility: visible !important; }
      #invoice-print-area { position: absolute !important; left: 0 !important; top: 0 !important; width: 100% !important; background: white !important; }
    }`;
    document.head.appendChild(style);
    return () => {
      document.getElementById(id)?.remove();
    };
  }, []);

  useEffect(() => {
    const n = Number(invoiceNumber);
    if (!Number.isFinite(n) || n <= 0) {
      setError("Invalid invoice number.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    CustomerInvoiceApi.getInvoiceByNumber(n)
      .then((res) => {
        setData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        if (err.response?.status === 404) {
          setError("Invoice not found, or it does not belong to your account.");
        } else if (err.response?.status === 403) {
          setError(
            err.response?.data?.message ??
              "Invoice lookup is not enabled for this account."
          );
        } else {
          setError("Could not load this invoice.");
        }
      });
  }, [invoiceNumber]);

  const handlePrint = () => window.print();

  if (loading) return <Loader />;

  if (error || !data) {
    return (
      <div className="w-full max-w-3xl mx-auto p-4">
        <p className="text-red-700 mb-4">{error}</p>
        <Link to="/my-invoices" className="text-blue-600 hover:underline">
          Back to invoices
        </Link>
      </div>
    );
  }

  const billBlock = (
    <>
      <div className="font-semibold text-gray-900">{data.customerName}</div>
      {data.customerAddress1 && (
        <div className="text-gray-800">{data.customerAddress1}</div>
      )}
      {data.customerAddress2 && (
        <div className="text-gray-800">{data.customerAddress2}</div>
      )}
      <div className="text-gray-800">
        {[data.customerCity, data.customerState, data.customerZip]
          .filter(Boolean)
          .join(", ")}
      </div>
      {data.customerContact && (
        <div className="text-gray-800 mt-1">{data.customerContact}</div>
      )}
    </>
  );

  const shipBlock =
    data.shipTo === true ||
    data.shipAddress1 ||
    data.shipCity ||
    data.shipZip ? (
      <>
        <div className="font-semibold text-gray-900 mb-1">Ship to</div>
        {data.shipAddress1 && (
          <div className="text-gray-800">{data.shipAddress1}</div>
        )}
        {data.shipAddress2 && (
          <div className="text-gray-800">{data.shipAddress2}</div>
        )}
        <div className="text-gray-800">
          {[data.shipCity, data.shipState, data.shipZip]
            .filter(Boolean)
            .join(", ")}
        </div>
      </>
    ) : null;

  return (
    <div className="w-full max-w-4xl mx-auto p-4">
      <div className="no-print flex flex-wrap gap-3 mb-4 items-center justify-between">
        <Link to="/my-invoices" className="text-blue-600 hover:underline">
          ← All invoices
        </Link>
        <div className="flex gap-2">
          <Button outline onClick={handlePrint}>
            Print / Save as PDF
          </Button>
        </div>
      </div>

      <div
        id="invoice-print-area"
        className="bg-white text-gray-900 border border-gray-300 rounded-lg p-8 shadow-sm"
      >
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-6 border-b border-gray-300 pb-6 mb-6">
          <div>
            <img src={PBLogo} alt="Price Bros" className="h-14 mb-2" />
            <div className="text-sm text-gray-800 leading-snug">
              <div className="font-semibold">Price Bros Equipment Co.</div>
              <div>Parts &amp; Equipment</div>
              <div className="mt-2 font-semibold text-red-600">Invoice</div>
            </div>
          </div>
          <div className="text-sm sm:text-right space-y-1">
            <div>
              <span className="text-gray-600">Invoice #</span>{" "}
              <span className="font-bold text-lg">{data.invoiceNumber}</span>
            </div>
            <div>
              <span className="text-gray-600">Document ID</span>{" "}
              {data.documentId}
            </div>
            <div>
              <span className="text-gray-600">Invoice date</span>{" "}
              {fmtDate(data.invoiceDate)}
            </div>
            <div>
              <span className="text-gray-600">Due date</span>{" "}
              {fmtDate(data.dueDate)}
            </div>
            {data.customerPo && (
              <div>
                <span className="text-gray-600">Customer PO</span>{" "}
                {data.customerPo}
              </div>
            )}
            {data.salesman && (
              <div>
                <span className="text-gray-600">Sales rep</span> {data.salesman}
              </div>
            )}
            {data.shippingDate && (
              <div>
                <span className="text-gray-600">Ship date</span>{" "}
                {fmtDate(data.shippingDate)}
              </div>
            )}
            {(data.shippingMethodType != null || data.shippingMethod != null) && (
              <div>
                <span className="text-gray-600">Ship method</span>{" "}
                <span className="font-medium text-gray-900">
                  {data.shippingMethodType ??
                    (data.shippingMethod != null
                      ? `Code ${data.shippingMethod}`
                      : "—")}
                </span>
                {data.shippingMethodType != null && data.shippingMethod != null && (
                  <span className="text-gray-500"> ({data.shippingMethod})</span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-8 text-sm">
          <div>
            <div className="font-semibold text-gray-900 mb-2">Bill to</div>
            {billBlock}
          </div>
          {shipBlock && <div>{shipBlock}</div>}
        </div>

        <table className="w-full text-sm mb-6">
          <thead>
            <tr className="border-b-2 border-gray-800 text-left">
              <th className="py-2 pr-2">Item #</th>
              <th className="py-2 pr-2">Description</th>
              <th className="py-2 pr-2 text-right">Qty</th>
              <th className="py-2 pr-2 text-right">Price</th>
              <th className="py-2 text-right">Ext</th>
            </tr>
          </thead>
          <tbody>
            {data.lines.map((line) => (
              <tr key={line.posdocumentDetailId} className="border-b border-gray-200">
                <td className="py-2 pr-2 align-top whitespace-nowrap">
                  {line.itemNumber}
                </td>
                <td className="py-2 pr-2 align-top">{line.description ?? ""}</td>
                <td className="py-2 pr-2 text-right align-top">
                  {line.quantity.toFixed(2)}
                </td>
                <td className="py-2 pr-2 text-right align-top">
                  {money(line.price)}
                </td>
                <td className="py-2 text-right align-top font-medium">
                  {money(line.extendedAmount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-end">
          <div className="w-full max-w-xs text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-gray-600">Lines subtotal</span>
              <span>{money(data.linesSubtotal)}</span>
            </div>
            {data.documentTaxTotal != null && data.documentTaxTotal !== 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Tax</span>
                <span>{money(data.documentTaxTotal)}</span>
              </div>
            )}
            <div className="flex justify-between text-base font-bold border-t border-gray-400 pt-2 mt-2">
              <span>Document total</span>
              <span>{money(data.documentTotal)}</span>
            </div>
            {data.amountAccount != null && data.amountAccount !== 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">On account</span>
                <span>{money(data.amountAccount)}</span>
              </div>
            )}
            {data.amountTendered != null && data.amountTendered !== 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Amount tendered</span>
                <span>{money(data.amountTendered)}</span>
              </div>
            )}
          </div>
        </div>

        {data.memo && (
          <div className="mt-8 pt-6 border-t border-gray-200 text-sm">
            <div className="font-semibold text-gray-900 mb-1">Memo</div>
            <div className="text-gray-800 whitespace-pre-wrap">{data.memo}</div>
          </div>
        )}
      </div>
    </div>
  );
}
